"use client";

import React from "react";
import Image from "next/image";
import { useSortable } from "@dnd-kit/sortable";
import { MdDelete } from "react-icons/md";
import { getMedia } from "@/lib/media/shared";
import styles from "./media.module.css";
import { LocalPreviewMediaItem } from "./photosUploader";

export interface SortablePhotoItemProps {
  photo: LocalPreviewMediaItem;
  onDelete: () => void;
  onClick: () => void;
}

export default function SortablePhotoItem({
  photo,
  onDelete,
  onClick,
}: SortablePhotoItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: photo.MediaId,
  });

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : 1,
  };

  // Safe because items here are always READY
  const resolvedMedia = getMedia(photo, "md");
  const displayUrl = photo.localUrl || resolvedMedia?.src || "";

  // 🚀 FIX 2: Detect if it's a blob so Next.js Image doesn't crash trying to optimize it
  const isBlob = displayUrl.startsWith("blob:");

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={styles.photoCard}
    >
      <button
        type="button"
        className={styles.deleteBtn}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete();
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <MdDelete size={18} />
      </button>

      {displayUrl && (
        <div
          className={styles.imageContainer}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          <Image
            src={displayUrl}
            alt={photo.OriginalFileName || "Property Media"}
            fill
            sizes="(max-width: 768px) 33vw, 20vw"
            className={styles.photoImage}
            unoptimized={isBlob}
          />
        </div>
      )}
    </div>
  );
}
